import { Users } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CreateUserDialog } from '@/components/admin/CreateUserDialog'
import { EditUserDialog } from '@/components/admin/EditUserDialog'
import { UserDetailCard } from '@/components/admin/UserDetailCard'
import { UserList } from '@/components/admin/UserList'
import { PageShell } from '@/components/layout/PageShell'
import { IDP_MANAGES_ACCOUNTS } from '@/config'
import { useRevealDetail } from '@/features/admin/hooks/useRevealDetail'
import { useUsersAdmin } from '@/features/admin/hooks/useUsersAdmin'

export default function AdminUsersPage() {
  const { t } = useTranslation()
  const {
    users,
    loading,
    selectedUserId,
    setSelectedUserId,
    selectedUser,
    createUser,
    updateUsername,
    updateRole,
    resetPassword,
  } = useUsersAdmin()

  const [showCreate, setShowCreate] = useState(false)
  const [showEdit, setShowEdit] = useState(false)
  const [creating, setCreating] = useState(false)
  const [savingUsername, setSavingUsername] = useState(false)
  const [savingRole, setSavingRole] = useState(false)
  const [savingPassword, setSavingPassword] = useState(false)
  const detail = useRevealDetail()

  const handleResetPassword = async (password: string) => {
    if (!selectedUser) return
    setSavingPassword(true)
    const ok = await resetPassword(selectedUser.id, password, selectedUser.username)
    setSavingPassword(false)
    if (ok) setShowEdit(false)
  }

  return (
    <PageShell
      layout="padded"
      icon={Users}
      title={t('admin.users.title')}
      summary={t(IDP_MANAGES_ACCOUNTS ? 'admin.users.summarySso' : 'admin.users.summary')}
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2">
          <UserList
            users={users}
            selectedUserId={selectedUserId}
            loading={loading}
            onSelect={id => {
              setSelectedUserId(id)
              detail.reveal()
            }}
            onCreateUser={IDP_MANAGES_ACCOUNTS ? undefined : () => setShowCreate(true)}
          />
        </div>
        <div ref={detail.ref} className="lg:col-span-3">
          <UserDetailCard user={selectedUser} onEdit={() => setShowEdit(true)} />
        </div>
      </div>

      {!IDP_MANAGES_ACCOUNTS && (
        <CreateUserDialog
          open={showCreate}
          onOpenChange={setShowCreate}
          submitting={creating}
          onSubmit={async input => {
            setCreating(true)
            const created = await createUser(input)
            setCreating(false)
            if (created) setShowCreate(false)
          }}
        />
      )}

      <EditUserDialog
        open={showEdit}
        onOpenChange={setShowEdit}
        user={selectedUser}
        savingUsername={savingUsername}
        savingRole={savingRole}
        savingPassword={savingPassword}
        onUpdateUsername={async username => {
          if (!selectedUser) return
          setSavingUsername(true)
          await updateUsername(selectedUser.id, username)
          setSavingUsername(false)
        }}
        onUpdateRole={async role => {
          if (!selectedUser) return
          setSavingRole(true)
          await updateRole(selectedUser.id, role)
          setSavingRole(false)
        }}
        onResetPassword={IDP_MANAGES_ACCOUNTS ? undefined : handleResetPassword}
      />
    </PageShell>
  )
}
